import React, { useEffect, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { useTheme } from '../../ThemeContext';
import ConnectGmail from './ConnectGmail';
import { formatPhone, formatPhoneInput, phoneForSave } from '../../utils/phone';
import './settings.css';

const Settings = () => {
  const { user, updateProfile } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [sharedInboxSignature, setSharedInboxSignature] = useState('');
  const [autoAssignAsInstaller, setAutoAssignAsInstaller] = useState(false);
  const [landingSection, setLandingSection] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  const hydrateFromUser = () => {
    setUsername(user?.username || '');
    setEmail(user?.email || '');
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setJobTitle(user?.jobTitle || '');
    setPhone(formatPhone(user?.phone || ''));
    setPhotoUrl(user?.photoUrl || '');
    setSharedInboxSignature(user?.sharedInboxSignature || '');
    setAutoAssignAsInstaller(!!user?.autoAssignAsInstaller);
    setLandingSection(user?.landingSection || '');
  };

  useEffect(() => {
    hydrateFromUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const startEdit = () => {
    hydrateFromUser();
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError('');
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    const result = await updateProfile({
      username: username.trim(),
      email: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      jobTitle: jobTitle.trim(),
      phone: phoneForSave(phone).trim(),
      photoUrl: photoUrl.trim(),
      sharedInboxSignature: sharedInboxSignature.trim(),
      autoAssignAsInstaller,
      landingSection: landingSection || null,
    });
    setSaving(false);
    if (result.success) {
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p className="settings-subtitle">Manage your account and integrations</p>
      </div>

      <div className="settings-grid">
        {/* Profile + Appearance combined */}
        <div className="settings-card">
          <div className="settings-card-header">
            <i className="fas fa-user"></i>
            <h3>Profile</h3>
          </div>
          <div className="settings-card-body">
            {error && <div className="error-message">{error}</div>}
            {savedFlash && <div className="success-message" style={{ color: 'var(--accent-green)', fontSize: '0.85rem' }}>Saved.</div>}

            {editing ? (
              <form onSubmit={handleSave} className="profile-edit-form">
                <div className="profile-edit-row">
                  <label htmlFor="profile-username">Username</label>
                  <input
                    id="profile-username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    minLength={1}
                    maxLength={50}
                    className="profile-edit-input"
                  />
                </div>
                <div className="profile-edit-row">
                  <label htmlFor="profile-email">Email</label>
                  <input
                    id="profile-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    maxLength={100}
                    className="profile-edit-input"
                  />
                </div>

                <div className="profile-signature-hint">
                  Used in your outgoing email signature.
                </div>

                <div className="profile-edit-row-group">
                  <div className="profile-edit-row">
                    <label htmlFor="profile-first-name">First Name</label>
                    <input
                      id="profile-first-name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      maxLength={50}
                      className="profile-edit-input"
                    />
                  </div>
                  <div className="profile-edit-row">
                    <label htmlFor="profile-last-name">Last Name</label>
                    <input
                      id="profile-last-name"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      maxLength={50}
                      className="profile-edit-input"
                    />
                  </div>
                </div>
                <div className="profile-edit-row">
                  <label htmlFor="profile-auto-installer">
                    <input
                      id="profile-auto-installer"
                      type="checkbox"
                      checked={autoAssignAsInstaller}
                      onChange={(e) => setAutoAssignAsInstaller(e.target.checked)}
                      style={{ marginRight: '8px' }}
                    />
                    I do my own work
                  </label>
                  <div className="profile-field-hint">
                    Puts you on a job as the installer when you are set as its sales rep,
                    so you do not have to name yourself twice. Only fills the field when
                    it is empty. Leave this off unless you actually do the work — the
                    installer is who receives the work-order texts.
                  </div>
                </div>

                <div className="profile-edit-row">
                  <label htmlFor="profile-landing">When I sign in, open</label>
                  <select
                    id="profile-landing"
                    value={landingSection}
                    onChange={(e) => setLandingSection(e.target.value)}
                    className="profile-edit-input"
                  >
                    <option value="">Tasks (default)</option>
                    <option value="projects">Projects</option>
                    <option value="customers">Contacts</option>
                    <option value="messages">Messages</option>
                    <option value="boards">Boards</option>
                  </select>
                  <div className="profile-field-hint">
                    Yours only — it changes nothing for anyone else. Crews tend to open
                    the app to look at jobs, office staff to work a task list, so there
                    is no one right answer to set for everybody.
                  </div>
                </div>

                <div className="profile-edit-row">
                  <label htmlFor="profile-shared-signature">Shared inbox signature</label>
                  <textarea
                    id="profile-shared-signature"
                    value={sharedInboxSignature}
                    onChange={(e) => setSharedInboxSignature(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Leave blank to sign as PRI Repairs Team"
                    className="profile-edit-input"
                  />
                  <div className="profile-field-hint">
                    Used only when you send from a shared address such as
                    repairs@proroofersinc.com. Mail from your own account keeps your
                    personal signature. Blank signs as <strong>PRI Repairs Team</strong>.
                  </div>
                </div>
                <div className="profile-edit-row">
                  <label htmlFor="profile-job-title">Job Title</label>
                  <input
                    id="profile-job-title"
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    maxLength={100}
                    placeholder="e.g. Project Manager"
                    className="profile-edit-input"
                  />
                </div>
                <div className="profile-edit-row">
                  <label htmlFor="profile-phone">Phone</label>
                  <input
                    id="profile-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                    maxLength={30}
                    placeholder="(555) 555-5555"
                    className="profile-edit-input"
                  />
                </div>
                <div className="profile-edit-row">
                  <label htmlFor="profile-photo-url">Photo URL</label>
                  <input
                    id="profile-photo-url"
                    type="url"
                    value={photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                    maxLength={500}
                    placeholder="https://…"
                    className="profile-edit-input"
                  />
                </div>

                <div className="profile-edit-actions">
                  <button type="button" onClick={cancelEdit} disabled={saving} className="profile-edit-btn">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving} className="profile-edit-btn primary-action">
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <div className="profile-field">
                  <label>Username</label>
                  <span>{user?.username}</span>
                </div>
                <div className="profile-field">
                  <label>Email</label>
                  <span>{user?.email}</span>
                </div>
                <div className="profile-field">
                  <label>Role</label>
                  <span className="role-badge">{user?.role}</span>
                </div>

                <div className="profile-section-label">Email signature</div>
                <div className="profile-field">
                  <label>Name</label>
                  <span>
                    {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || (
                      <span className="profile-field-empty">—</span>
                    )}
                  </span>
                </div>
                <div className="profile-field">
                  <label>Job Title</label>
                  <span>{user?.jobTitle || <span className="profile-field-empty">—</span>}</span>
                </div>
                <div className="profile-field">
                  <label>Phone</label>
                  <span>{formatPhone(user?.phone) || <span className="profile-field-empty">—</span>}</span>
                </div>
                <div className="profile-field">
                  <label>Photo</label>
                  <span>
                    {user?.photoUrl ? (
                      <img
                        src={user.photoUrl}
                        alt="Profile"
                        className="profile-photo-preview"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="profile-field-empty">—</span>
                    )}
                  </span>
                </div>

                <button type="button" onClick={startEdit} className="profile-edit-btn primary-action profile-edit-trigger">
                  <i className="fas fa-pen"></i> Edit Profile
                </button>
              </>
            )}

            <div className="profile-divider" />

            <div className="theme-toggle-row">
              <span>Dark Mode</span>
              <button
                type="button"
                onClick={toggleTheme}
                className="theme-toggle-switch-btn"
                aria-pressed={isDark}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                <span
                  className="theme-toggle-track"
                  style={{
                    background: isDark ? '#2f81f7' : '#cbd5e1',
                  }}
                >
                  <span
                    className="theme-toggle-knob"
                    style={{ left: isDark ? '18px' : '2px' }}
                  />
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Gmail Integration */}
        <ConnectGmail />
      </div>
    </div>
  );
};

export default Settings;
